import React from 'react'
import {
  Col,
  InputNumber,
  Input,
  PageHeader,
  Row,
  Form,
  Tooltip,
  Button,
  message
} from 'antd'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { Store } from '@slerp/controls'
import { useQuery } from '@apollo/client'
import StripeConnectStoreSettings from '../StripeConnectStoreSettings'
import { useMutation } from '@apollo/client'
import Loading from 'components/Utils/Loading'
import {
  ATTRIBUTIONS_MAP,
  attributionToFormObject,
  chunkArray
} from 'components/Settings/AccountsAndBilling/utils'
import styled from '@emotion/styled'
import { useSession } from 'packages/@slerp/accounts'
import { QUERY_GET_STORE_FRANCHISE_ATTRIBUTION } from '../LocationQueries'
import { UPDATE_STORE } from './actions'

interface Props {
  store: Store
}

const { useForm } = Form

const Account = ({ store }: Props) => {
  const [attributionForm] = useForm()
  const [emailForm] = useForm()
  const { user } = useSession()

  const { data, loading } = useQuery(QUERY_GET_STORE_FRANCHISE_ATTRIBUTION, {
    variables: {
      storeId: store.id
    }
  })

  const attribution = attributionToFormObject(
    data?.getStoreFranchiseAttributions || {},
    { setDefault: false }
  )

  const [updateStore] = useMutation(UPDATE_STORE, {
    refetchQueries: ['getStore'],
    onCompleted: () => {
      message.destroy()
      message.success('Accounts email updated successfully!')
    },
    onError: () => {
      message.destroy()
      message.error('Failed to update accounts email')
    }
  })

  const handleSaveEmail = (values: { accounts_email: string }) => {
    updateStore({
      variables: {
        store_id: store.id,
        accounts_email: values.accounts_email
      }
    })
  }

  const attributionChunk = chunkArray(Object.entries(ATTRIBUTIONS_MAP), 3)
  const rolesAllowedToConnect = ['admin', 'manager']

  return (
    <>
      {loading && <Loading />}
      {!loading && (
        <>
          <Row>
            <Col span={16}>
              <StripeConnectStoreSettings
                canConnect={rolesAllowedToConnect.includes(user?.role || '')}
                store={store}
                stripeId={
                  data?.getStoreFranchiseAttributions?.stripeUserId || ''
                }
              />
            </Col>
          </Row>

          <Form
            data-testid='accountsEmail'
            id='accountsEmail'
            form={emailForm}
            onFinish={handleSaveEmail}
            layout='vertical'
            initialValues={{
              accounts_email: store.accounts_email
            }}
          >
            <Row gutter={20}>
              <Col span={6}>
                <Form.Item
                  style={{ margin: 0 }}
                  name='accounts_email'
                  label={
                    <>
                      Accounts Email
                      <Tooltip
                        title={
                          <span>
                            This email address will receive invoices and
                            payment-related notifications.
                          </span>
                        }
                      >
                        <QuestionCircleOutlined className='_ml-8' />
                      </Tooltip>
                    </>
                  }
                  rules={[
                    {
                      required: true,
                      message: 'Accounts email is required'
                    },
                    {
                      type: 'email',
                      message: 'Please input a valid email'
                    }
                  ]}
                  required
                >
                  <Input
                    placeholder='Store email'
                    disabled={!rolesAllowedToConnect.includes(user?.role || '')}
                  />
                </Form.Item>
              </Col>
              <Col span={2}>
                <SaveEmailButton htmlType='submit'>Save</SaveEmailButton>
              </Col>
            </Row>
          </Form>

          {attribution && (
            <>
              <PageHeader
                title={
                  <>
                    Attribution
                    <Tooltip
                      title={
                        <span>
                          Attribution determines the proportion of the partner
                          amount allocated to the location. For example, if the
                          attribution is 90%, the location will receive 90% of
                          the partner amount and the partner will receive the
                          balance 10%.
                        </span>
                      }
                    >
                      <QuestionCircleOutlined className='_ml-8' />
                    </Tooltip>
                  </>
                }
              />
              <Form
                data-testid='storeAttribution'
                id='storeAttribution'
                form={attributionForm}
                layout='vertical'
                initialValues={attribution}
              >
                {attributionChunk.map((row, index) => (
                  <Row key={index} className='_mb-32' gutter={[40, 40]}>
                    {row.map(([key, label]) => (
                      <Col span={6} key={key}>
                        <Form.Item
                          label={label}
                          name={key}
                          className='_mb-0 -plain'
                        >
                          <DisabledInput
                            disabled
                            data-testid={key}
                            style={{ width: '100%' }}
                            formatter={(value: number | string | undefined) =>
                              value ? `${value}%` : ''
                            }
                          />
                        </Form.Item>
                      </Col>
                    ))}
                  </Row>
                ))}
              </Form>
            </>
          )}
        </>
      )}
    </>
  )
}
export default Account

const DisabledInput = styled(InputNumber)(() => ({
  '&.ant-input-number-disabled': {
    color: 'black',
    backgroundColor: 'transparent'
  }
}))

const SaveEmailButton = styled(Button)`
  height: 36px;
  margin-top: 30px;
`
